import { Tooltip } from '@civilgrid/ui/components';
import { centroid, polygon } from '@turf/turf';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { MapObject, Project } from '~/common/types';
import Icon from '~/components2/Icon';
import { Notify } from '~/components2/Notify';
import { useCivilDrawStore } from '../../civilDrawStoreContext';

interface ObjectDetailsProps {
  currentObject: MapObject;
}

export const ObjCoordinates = ({ currentObject }: ObjectDetailsProps) => {
  const store = useCivilDrawStore();
  const { currentMapId, project } = useSnapshot(store) as typeof store;
  const pointRef = useMemo(() => {
    if (
      currentObject.type === 'polygon' ||
      currentObject.type === 'textV2' ||
      currentObject.type === 'image' ||
      currentObject.type === 'stamp'
    ) {
      const point = polygon(currentObject.geometry.coordinates);
      const center = centroid(point);

      return {
        lng: center.geometry.coordinates[0],
        lat: center.geometry.coordinates[1],
        tooltipText: 'Center of object',
      };
    } else if (currentObject.type === 'line' || currentObject.type === 'arrow') {
      const lastPoint = currentObject.geometry.coordinates[currentObject.geometry.coordinates.length - 1];

      const tooltipText = currentObject.type === 'arrow' ? 'End of arrow' : 'End of object';

      return {
        lng: lastPoint[0],
        lat: lastPoint[1],
        tooltipText,
      };
    } else if (currentObject.type === 'marker' || currentObject.type === 'text' || currentObject.type === 'circle') {
      // NOTE: although types, 'streetViewMarker' & 'comment', do not have object details they would be handled here.
      const point = currentObject.geometry.coordinates;
      return {
        lng: point[0],
        lat: point[1],
        tooltipText: 'Center of object',
      };
    }
  }, [currentObject]);

  return (
    <div className="mt-6 flex flex-col">
      <span className="text-base font-bold">Coordinates</span>
      <div className="mt-4 gap-y-2">
        <div className="flex items-center">
          <Icon icon="location" />
          <span className="ml-1 mr-2 select-none">
            <Tooltip
              text={pointRef?.tooltipText}
              delayDuration={200}
              sideOffset={10}
              data-testid="civildraw-object-coordinates"
            >
              {pointRef?.lat.toFixed(5)},&nbsp;{pointRef?.lng.toFixed(5)}
            </Tooltip>
          </span>
          <Tooltip asChild text="Copy coordinates" delayDuration={200} sideOffset={10}>
            <button
              type="button"
              aria-label="Copy coordinates"
              data-testid="civildraw-object-coordinates-copy"
              className="flex cursor-pointer appearance-none rounded border-0 bg-transparent p-0 focus-ring"
              onClick={() => {
                navigator.clipboard.writeText(`${pointRef?.lat.toFixed(5)}, ${pointRef?.lng.toFixed(5)}`);
                Notify.info({ message: 'Coordinate copied to clipboard' });
                const analyticsProject = project as unknown as Project;
                captureTypedPosthogEvent(POSTHOG_EVENTS.civildrawObjectCoordinatesCopied, {
                  project_id: analyticsProject.id,
                  organization_id: analyticsProject.organization || undefined,
                  object_type: mapObjectTypeToCivildrawObjectType(currentObject.type),
                  ...(currentMapId && { map_id: currentMapId }),
                });
              }}
            >
              <Icon icon="copy" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
